/**
 * Billing — Shopify Billing API.
 *
 * Two things here are easy to get wrong and both are review failures:
 *
 *  1. **The redirect must escape the iframe.** We render inside the Shopify
 *     admin, and Shopify's charge-approval page sets frame-ancestor headers that
 *     refuse to load in a nested frame. A plain `window.location.href` silently
 *     produces a blank rectangle. It has to be `window.top.location`.
 *
 *  2. **No pop-ups.** Requirement: apps "shouldn't use pop-up windows for
 *     essential app functionality, like running OAuth or approving app charges."
 *     `window.open` would also get blocked by the browser half the time.
 *
 * And the thing that isn't obvious from the code: clicking a plan changes
 * nothing. The server returns a confirmation URL, the merchant approves on
 * Shopify's own page, and only then does the plan move. If this screen ever
 * shows a success state before that round trip, it is lying.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, fmt, ApiError, type ShopifyBillingState } from '../api';

export default function Billing() {
  const [state, setState] = useState<ShopifyBillingState | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .billing()
      .then(setState)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Could not load billing.'));
  }, []);

  useEffect(() => {
    load();
    // Shopify appends ?checkout= when it sends the merchant back through the
    // interstitial. Surface the outcome rather than leaving them guessing.
    const p = new URLSearchParams(window.location.search);
    if (p.get('checkout') === 'complete') setNotice('Plan updated. Thanks!');
    if (p.get('checkout') === 'cancelled') setNotice('No change was made.');
  }, [load]);

  async function choose(plan: 'starter' | 'growth' | 'scale') {
    setBusy(plan);
    setError(null);
    try {
      const { confirmationUrl } = await api.changePlan(plan);
      // Break out of the Shopify admin iframe. Anything else renders blank.
      if (window.top) window.top.location.href = confirmationUrl;
      else window.location.href = confirmationUrl;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not start the plan change.');
      setBusy(null);
    }
  }

  async function cancel() {
    if (!window.confirm('Cancel your subscription? Your campaigns keep running on the ad platforms and nothing is deleted.')) {
      return;
    }
    setBusy('cancel');
    setError(null);
    try {
      await api.cancelPlan();
      setNotice('Subscription cancelled. Your data and campaigns are untouched.');
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not cancel.');
    } finally {
      setBusy(null);
    }
  }

  if (error && !state) return <div className="empty">{error}</div>;
  if (!state) return <div className="empty">Loading…</div>;

  return (
    <>
      <p className="eyebrow">Setup</p>
      <h1 className="pg-title">Billing</h1>
      <p className="pg-sub">
        Charged through Shopify, so ScaleAds AI appears on the same invoice as your Shopify subscription.
        Change plan or cancel here — you never need to contact us to do either.
      </p>

      {notice && <p className="ok-line" style={{ marginBottom: 16 }}>{notice}</p>}
      {error && <p className="err-line" style={{ marginBottom: 16 }}>{error}</p>}

      <div className="card card-p">
        <h4>
          Current plan
          <span className="meta">
            {state.hasActiveSubscription ? 'active subscription' : 'no active subscription'}
          </span>
        </h4>
        <div className="grid-3">
          <div className="metric">
            <div className="lbl">Plan</div>
            <div className="val">{state.planName.replace('ScaleAds AI ', '')}</div>
            <div className="delta flat">${state.priceUsd}/month</div>
          </div>
          <div className="metric">
            <div className="lbl">Status</div>
            <div
              className="val"
              style={{
                color: state.hasActiveSubscription ? 'var(--success)' : 'var(--warning)',
                fontSize: 20,
              }}
            >
              {state.status}
            </div>
            {state.trialEndsAt && (
              <div className="delta flat">renews {new Date(state.trialEndsAt).toLocaleDateString('en-AU')}</div>
            )}
          </div>
          <div className="metric">
            <div className="lbl">Ad spend this month</div>
            <div className="val">{fmt.moneyShort(state.mtd.ad_spend_cents)}</div>
            <div className="delta flat">
              {fmt.moneyShort(state.mtd.ad_revenue_cents)} revenue attributed
            </div>
          </div>
        </div>
        <p className="footnote">
          Your plan price does not depend on your ad spend — it's shown here only so you can see
          whether you're on the right tier. We deliberately don't charge a percentage of spend,
          because that would pay us to grow your spend rather than your profit.
        </p>
      </div>

      <div className="card card-p">
        <h4>
          Change plan
          <span className="meta">upgrade or downgrade any time</span>
        </h4>
        <div className="channel-grid">
          {state.availablePlans.map((p) => {
            const current = p.key === state.plan && state.hasActiveSubscription;
            return (
              <article key={p.key} className={`card card-p channel ${current ? 'on' : ''}`}>
                <div className="channel-head">
                  <h3>{p.name.replace('ScaleAds AI ', '')}</h3>
                  {current && <span className="badge b-active">current</span>}
                </div>
                <div className="bench-value" style={{ margin: '6px 0 4px' }}>
                  ${p.priceUsd}
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-mute)' }}>
                    /mo
                  </span>
                </div>
                <p className="channel-edge">{p.blurb}</p>
                <div className="channel-foot">
                  {current ? (
                    <span className="meta">You're on this plan</span>
                  ) : (
                    <button
                      className="btn sm brand"
                      onClick={() => void choose(p.key)}
                      disabled={busy !== null}
                    >
                      {busy === p.key ? 'Opening Shopify…' : `Switch to ${p.name.replace('ScaleAds AI ', '')}`}
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
        <p className="footnote">
          Choosing a plan sends you to Shopify to approve the charge — nothing changes until you do,
          and you can back out at that point. Shopify prorates automatically, so switching mid-cycle
          doesn't cost you a full extra month.
        </p>
      </div>

      {state.hasActiveSubscription && (
        <div className="card card-p">
          <h4>Cancel</h4>
          <p className="pg-sub" style={{ marginBottom: 12 }}>
            Your campaigns keep running on the ad platforms under their own settings, and nothing is
            deleted. We stop automating, and you stop paying. If you come back, everything is where
            you left it.
          </p>
          <button className="btn danger" onClick={() => void cancel()} disabled={busy !== null}>
            {busy === 'cancel' ? 'Cancelling…' : 'Cancel subscription'}
          </button>
        </div>
      )}
    </>
  );
}
